// The tasting form. One <form> holds every field and its steps come from the
// markup (one panel per step, named by data-step-name); this controller
// shows one step at a time, keeps the two star rows and the two note
// textareas in sync, locks the stars to the BLIC count, carries aroma
// descriptors into an empty flavour list on entering Taste, and writes the
// read-only summary. Ported from the legacy TastingFlow (Tasting.tsx).
import { Controller } from "@hotwired/stimulus"

const DEFAULT_STARS = 3
// WSET quality levels, by star.
const QUALITY = ["", "Poor", "Acceptable", "Good", "Very Good", "Outstanding"]
const DISCARD = "Discard this note?"

export default class extends Controller {
  static targets = [
    "title", "tabs", "tab", "panel",
    "starInput", "starGroup", "blic", "notes",
    "dependent", "quality",
    "noseTags", "flavorTags",
    "summary", "summaryList",
    "quickActions", "stepActions", "next", "saveNote"
  ]
  static values = { step: { type: Number, default: 0 }, labels: Object, dirty: { type: Boolean, default: false } }

  connect() {
    this.names = this.panelTargets.map((panel) => panel.dataset.stepName)
    this.lastStep = this.names.length - 1
    // Read before show() labels any tab: a tab's name is its own text.
    this.tabNames = this.tabTargets.map((tab) => tab.textContent.trim())
    // A form re-rendered after a refusal already holds the taster's values.
    this.dirty = this.dirtyValue
    this.leaving = false
    // Leaving with a half-written note asks first. Turbo raises before-visit
    // for link visits and also for the redirect that follows a form
    // submission, this form's or any other on the page (Sign out). A
    // submission has already happened by then, so asking would be too late:
    // `leaving` is set when one starts and cleared again if it fails.
    this.guard = (event) => {
      if (this.dirty && !this.leaving && !window.confirm(DISCARD)) event.preventDefault()
    }
    this.submitStarted = () => { this.leaving = true }
    this.submitEnded = (event) => { if (!event.detail.success) this.leaving = false }
    document.addEventListener("turbo:before-visit", this.guard)
    document.addEventListener("turbo:submit-start", this.submitStarted)
    document.addEventListener("turbo:submit-end", this.submitEnded)
    this.show(this.stepValue)
    this.paintStars()
    this.toggleDependents()
    this.refresh()
  }

  disconnect() {
    document.removeEventListener("turbo:before-visit", this.guard)
    document.removeEventListener("turbo:submit-start", this.submitStarted)
    document.removeEventListener("turbo:submit-end", this.submitEnded)
  }

  // Any edit: the note is now worth asking about, dependents may show or
  // hide, and the summary may have changed.
  changed() {
    this.dirty = true
    this.toggleDependents()
    this.refresh()
  }

  submitting() {
    this.leaving = true
  }

  // The guest flow's own submit handler ran submitting() before finding out
  // localStorage was full; no Turbo submission ever starts or ends to clear
  // the flag, so guest-note#save dispatches this to rearm the guard itself.
  stayed() {
    this.leaving = false
  }

  // ---- steps ---------------------------------------------------------------

  goTo(event) {
    this.show(Number(event.params.step))
  }

  next() {
    const step = Math.min(this.lastStep, this.stepValue + 1)
    if (this.hasFlavorTagsTarget && this.panelTargets[step].contains(this.flavorTagsTarget)) this.carryOver()
    this.show(step)
  }

  prev() {
    this.show(Math.max(0, this.stepValue - 1))
  }

  show(step) {
    this.stepValue = step
    this.panelTargets.forEach((panel) => { panel.hidden = Number(panel.dataset.step) !== step })
    this.tabsTarget.hidden = step === 0
    this.tabTargets.forEach((tab, index) => {
      const s = Number(tab.dataset.step)
      tab.classList.toggle("on", s === step)
      tab.classList.toggle("done", s < step)
      tab.setAttribute("aria-pressed", String(s === step))
      // The tick on a finished step is drawn in CSS, so it is said here.
      if (s < step) tab.setAttribute("aria-label", `${this.tabNames[index]} (done)`)
      else tab.removeAttribute("aria-label")
    })
    this.titleTarget.textContent = this.names[step]
    this.quickActionsTarget.hidden = step !== 0
    this.stepActionsTarget.hidden = step === 0
    this.nextTarget.hidden = step >= this.lastStep
    if (step > 0 && step < this.lastStep) this.nextTarget.textContent = `Next: ${this.names[step + 1]} →`
    const last = step === this.lastStep
    this.saveNoteTarget.classList.toggle("btn-primary", last)
    this.saveNoteTarget.classList.toggle("btn-outline-primary", !last)
    if (step === this.lastStep) this.refresh()
    window.scrollTo({ top: 0 })
  }

  // ---- stars and BLIC ------------------------------------------------------

  rate(event) {
    if (this.blicLocked) return
    this.starInputTarget.value = event.params.n
    this.dirty = true
    this.paintStars()
  }

  blicChanged() {
    this.paintStars()
  }

  get blicLocked() {
    return this.blicTargets.some((box) => box.checked)
  }

  get derivedStars() {
    return this.blicTargets.filter((box) => box.checked).length + 1
  }

  get stars() {
    if (this.blicLocked) return this.derivedStars
    return Number(this.starInputTarget.value) || DEFAULT_STARS
  }

  paintStars() {
    const locked = this.blicLocked
    const n = this.stars
    this.starGroupTargets.forEach((group) => {
      group.classList.toggle("stars-locked", locked)
      group.setAttribute("aria-label", `${n} of 5 stars`)
      group.querySelectorAll("[data-n]").forEach((star) => {
        const on = Number(star.dataset.n) <= n
        star.classList.toggle("on", on)
        star.setAttribute("aria-pressed", String(on))
        star.disabled = locked
      })
    })
    this.qualityTargets.forEach((el) => { el.textContent = QUALITY[n] })
  }

  // ---- notes, fault, carry-over -------------------------------------------

  // Step 0's Comments and Score's Notes are the same field.
  syncNotes(event) {
    this.notesTargets.forEach((area) => { if (area !== event.target) area.value = event.target.value })
  }

  // A field with show_when (Tasting::Field) appears only while the field it
  // depends on holds that value. Hidden, its controls are disabled too, so
  // a value typed before it was hidden is not posted (nor read by the guest
  // flow's FormData).
  toggleDependents() {
    this.dependentTargets.forEach((el) => {
      el.hidden = this.field(el.dataset.dependsOn) !== el.dataset.dependsValue
      el.querySelectorAll("input, textarea, select").forEach((control) => { control.disabled = el.hidden })
    })
  }

  // Entering Taste with no flavour descriptors starts from the aroma ones.
  carryOver() {
    const nose = this.tagsController("noseTags")
    const flavor = this.tagsController("flavorTags")
    if (nose && flavor && flavor.values.length === 0 && nose.values.length > 0) flavor.setValues(nose.values)
  }

  tagsController(name) {
    if (!this[`has${name[0].toUpperCase()}${name.slice(1)}Target`]) return null
    return this.application.getControllerForElementAndIdentifier(this[`${name}Target`], "descriptor-tags")
  }

  // Enter in a text field must not submit a half-written note.
  keydown(event) {
    if (event.key !== "Enter") return
    if (event.target.matches("input:not([type=checkbox]):not([type=radio])")) event.preventDefault()
  }

  // ---- summary -------------------------------------------------------------

  refresh() {
    if (!this.hasSummaryTarget) return
    const rows = this.summaryRows()
    this.summaryListTarget.replaceChildren(...rows.map(([term, value]) => {
      const row = document.createElement("div")
      row.className = "summary-row"
      const dt = document.createElement("dt")
      dt.textContent = `${term}:`
      const dd = document.createElement("dd")
      dd.textContent = value
      row.append(dt, dd)
      return row
    }))
    this.summaryTarget.hidden = rows.length === 0
  }

  summaryRows() {
    const label = (key) => (key ? (this.labelsValue[key] || key) : "")
    const value = (name) => label(this.field(name))
    const rows = []

    const colorBits = [this.field("color"), value("color_intensity")].filter(Boolean)
    if (colorBits.length) rows.push(["Color", colorBits.join(", ")])
    if (value("clarity")) rows.push(["Clarity", value("clarity")])

    const noseStructure = [value("nose_intensity"), value("nose_development")].filter(Boolean).join(", ")
    const nose = [noseStructure, this.list("nose_descriptors").join(", ")].filter(Boolean).join(" — ")
    if (nose) rows.push(["Aroma", nose])

    const structure = [
      value("sweetness"),
      value("acidity") && `${value("acidity")} acid`,
      value("tannin") && `${value("tannin")} tannin`,
      value("body") && `${value("body")} body`,
      value("alcohol") && `${value("alcohol")} alc`
    ].filter(Boolean)
    if (structure.length) rows.push(["Structure", structure.join(", ")])

    const flavor = this.list("flavor_descriptors")
    if (flavor.length) rows.push(["Flavor", flavor.join(", ")])
    if (value("finish_length")) rows.push(["Finish", value("finish_length")])

    const notes = (this.field("finish_notes") || "").trim()
    if (notes) rows.push(["Notes", notes])
    return rows
  }

  field(name) {
    const element = this.element.elements[`tasting_note[${name}]`]
    if (!element) return ""
    // A lone radio or checkbox is the element itself, not a RadioNodeList,
    // and its value reads the same whether or not it is checked.
    if (element instanceof HTMLInputElement && ["radio", "checkbox"].includes(element.type)) {
      return element.checked ? element.value : ""
    }
    return element.value || ""
  }

  list(name) {
    return this.field(name).split(",").map((item) => item.trim()).filter(Boolean)
  }
}
